/**
 * 命令面板（M4-10 / DESIGN.md §6.6，Cmd/Ctrl+K）。
 *
 * 纯展示组件：全部动作进、执行意图出，过滤/排序用 app 层的
 * `filterPaletteEntries`（分层规则——UI 层可以调 app 层的纯函数，
 * 但不持有 ActionRegistry/dispatch）。方向键选择、Enter 执行、Esc 关闭
 * 全部在输入框的 onKeyDown 里处理——面板打开时输入框自动聚焦。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createMemo, createSignal } from 'solid-js';
import { filterPaletteEntries, type PaletteEntry } from '../../app/actions/registry';
import { formatShortcut } from '../../app/actions/keymap';
import styles from './CommandPalette.module.css';

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly entries: readonly PaletteEntry[];
  readonly onExecute: (id: string) => void;
  readonly onClose: () => void;
}

export function CommandPalette(props: CommandPaletteProps): JSX.Element {
  const [query, setQuery] = createSignal('');
  const [activeIndex, setActiveIndex] = createSignal(0);
  let input: HTMLInputElement | undefined;

  const filtered = createMemo((): readonly PaletteEntry[] => filterPaletteEntries(props.entries, query()));

  createEffect(() => {
    // 打开时重置查询/选中项并聚焦输入框——每次都是一次全新的搜索会话
    if (props.open) {
      setQuery('');
      setActiveIndex(0);
      queueMicrotask(() => input?.focus());
    }
  });

  const clampIndex = (index: number, length: number): number => {
    if (length === 0) return 0;
    return ((index % length) + length) % length;
  };

  const execute = (entry: PaletteEntry | undefined): void => {
    if (entry === undefined || !entry.enabled) return;
    props.onExecute(entry.id);
    props.onClose();
  };

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="command-palette-backdrop">
        <div
          class={styles.panel}
          role="dialog"
          aria-label="命令面板"
          data-testid="command-palette"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <input
            ref={(el) => {
              input = el;
            }}
            class={styles.input}
            type="text"
            placeholder="搜索命令…"
            value={query()}
            data-testid="command-palette-input"
            onInput={(event) => {
              setQuery(event.currentTarget.value);
              setActiveIndex(0);
            }}
            onKeyDown={(event) => {
              const list = filtered();
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((i) => clampIndex(i + 1, list.length));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((i) => clampIndex(i - 1, list.length));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                execute(list[activeIndex()]);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                props.onClose();
              }
            }}
          />
          <ul class={styles.list} data-testid="command-palette-list">
            <For each={filtered()}>
              {(entry, index) => (
                <li
                  class={`${styles.item} ${index() === activeIndex() ? styles.active : ''} ${
                    entry.enabled ? '' : styles.disabled
                  }`}
                  data-testid={`command-palette-item-${entry.id}`}
                  onMouseEnter={() => setActiveIndex(index())}
                  onClick={() => execute(entry)}
                >
                  <span class={styles.itemGroup}>{entry.group}</span>
                  <span class={styles.itemTitle}>{entry.title}</span>
                  <Show when={entry.shortcut}>
                    {(shortcut) => <span class={styles.itemShortcut}>{formatShortcut(shortcut())}</span>}
                  </Show>
                </li>
              )}
            </For>
            <Show when={filtered().length === 0}>
              <li class={styles.empty}>没有匹配的命令</li>
            </Show>
          </ul>
        </div>
      </div>
    </Show>
  );
}
